import { supabase } from '../supabaseClient';

const DEFAULT_EVENTS = [
  'ticket_assigned',
  'mentioned',
  'work_item_comment',
  'deliverable_handoff',
];

/**
 * 取得使用者的通知偏好設定
 */
export const getNotificationPreferences = async (userId) => {
  try {
    const { data, error } = await supabase
      .from('notification_preferences')
      .select('*')
      .eq('user_id', userId);

    if (error) throw error;

    // Fill in defaults for missing event types
    const prefsMap = {};
    (data || []).forEach((pref) => {
      prefsMap[pref.event_type] = pref;
    });

    const fullPrefs = DEFAULT_EVENTS.map((eventType) => ({
      user_id: userId,
      event_type: eventType,
      in_app: prefsMap[eventType]?.in_app ?? true,
      email: prefsMap[eventType]?.email ?? true,
    }));

    return { data: fullPrefs, error: null };
  } catch (error) {
    console.error('Error fetching notification preferences:', error);
    return { data: null, error };
  }
};

/**
 * 更新通知偏好設定
 */
export const updateNotificationPreference = async (userId, eventType, updates) => {
  try {
    const { data, error } = await supabase
      .from('notification_preferences')
      .upsert(
        {
          user_id: userId,
          event_type: eventType,
          ...updates,
        },
        { onConflict: 'user_id,event_type' }
      )
      .select()
      .single();

    if (error) throw error;
    return { data, error: null };
  } catch (error) {
    console.error('Error updating notification preference:', error);
    return { data: null, error };
  }
};

/**
 * 檢查使用者是否啟用特定通知
 * @returns {{ in_app: boolean, email: boolean }}
 */
export const isNotificationEnabled = async (userId, eventType) => {
  try {
    const { data, error } = await supabase
      .from('notification_preferences')
      .select('in_app, email')
      .eq('user_id', userId)
      .eq('event_type', eventType)
      .maybeSingle();

    if (error) throw error;

    // Default: both enabled
    return {
      in_app: data?.in_app ?? true,
      email: data?.email ?? true,
    };
  } catch (error) {
    console.error('Error checking notification preference:', error);
    return { in_app: true, email: true };
  }
};

/**
 * 批次檢查多位使用者的通知偏好（mention 場景用）
 * @returns {Object<string, { in_app: boolean, email: boolean }>}
 */
export const isNotificationEnabledBatch = async (userIds, eventType) => {
  if (!userIds.length) return {};
  try {
    const { data, error } = await supabase
      .from('notification_preferences')
      .select('user_id, in_app, email')
      .in('user_id', userIds)
      .eq('event_type', eventType);

    if (error) throw error;

    const prefsMap = {};
    (data || []).forEach((p) => {
      prefsMap[p.user_id] = p;
    });

    // 沒設定的 user 預設全開
    return Object.fromEntries(
      userIds.map((uid) => [
        uid,
        {
          in_app: prefsMap[uid]?.in_app ?? true,
          email: prefsMap[uid]?.email ?? true,
        },
      ])
    );
  } catch (error) {
    console.error('Error batch checking notification preferences:', error);
    // fallback: 全部預設開啟
    return Object.fromEntries(userIds.map((uid) => [uid, { in_app: true, email: true }]));
  }
};
